import React, { useState, useEffect } from 'react';
import { Target, Plus, Edit, CheckCircle, X, Calendar } from 'lucide-react';
import { Button, Card, Input, Select, Alert, Spinner, Badge } from '../design-system';
import therapeuticGoalsService from '../../services/therapeuticGoalsService';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'react-hot-toast';

/**
 * Therapeutic Goals Component for Therapists
 * Allows therapists to add and manage therapeutic goals for patients
 */
const TherapeuticGoals = ({ patient }) => {
  const { user } = useAuth();
  const [goals, setGoals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingGoal, setEditingGoal] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (patient?.patientId) {
      loadGoals();
    }
  }, [patient?.patientId]);

  const loadGoals = async () => {
    try {
      setLoading(true);
      const response = await therapeuticGoalsService.getGoals(user, patient.patientId);
      setGoals(response.goals || []);
    } catch (error) {
      console.error('Error loading therapeutic goals:', error);
      toast.error('Failed to load therapeutic goals');
    } finally {
      setLoading(false);
    }
  };

  const handleAddGoal = () => {
    setEditingGoal(null);
    setShowAddModal(true);
  };

  const handleEditGoal = (goal) => {
    setEditingGoal(goal);
    setShowAddModal(true);
  };

  const handleDeleteGoal = async (goalId) => {
    if (!window.confirm('Are you sure you want to delete this goal?')) {
      return;
    }

    try {
      await therapeuticGoalsService.deleteGoal(user, goalId);
      toast.success('Goal deleted successfully');
      loadGoals();
    } catch (error) {
      console.error('Error deleting goal:', error);
      toast.error(error.message || 'Failed to delete goal');
    }
  };

  const handleSaveGoal = async (goalData) => {
    try {
      setSaving(true);
      therapeuticGoalsService.validateGoalData(goalData);

      if (editingGoal) {
        await therapeuticGoalsService.updateGoal(user, editingGoal.goalId, goalData);
        toast.success('Goal updated successfully');
      } else {
        await therapeuticGoalsService.createGoal(user, patient.patientId, goalData);
        toast.success('Goal added successfully');
      }

      setShowAddModal(false);
      setEditingGoal(null);
      loadGoals();
    } catch (error) {
      console.error('Error saving goal:', error);
      toast.error(error.message || 'Failed to save goal');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Card padding="large">
        <div className="flex items-center justify-center py-8">
          <div className="flex items-center gap-3">
            <Spinner size="medium" />
            <span className="text-gray-600">Loading therapeutic goals...</span>
          </div>
        </div>
      </Card>
    );
  }

  return (
    <>
      <Card padding="large">
        <div className="flex items-center justify-between mb-4 md:mb-6">
          <div className="flex items-center space-x-3 md:space-x-4">
            <div className="h-10 w-10 md:h-12 md:w-12 rounded-full flex items-center justify-center flex-shrink-0" style={{backgroundColor: 'rgba(44, 166, 164, 0.1)'}}>
              <Target className="h-5 w-5 md:h-6 md:w-6" style={{color: 'var(--color-primary)'}} />
            </div>
            <h3 className="text-lg md:text-xl font-semibold text-gray-900">Therapeutic Goals</h3>
          </div>
          <Button 
            onClick={handleAddGoal}
            variant="outline"
            size="small"
            className="flex items-center gap-2"
          >
            <Plus className="h-4 w-4" />
            <span className="hidden sm:inline">Add</span>
          </Button>
        </div>
        
        {goals.length === 0 ? (
          <div className="text-center py-8">
            <Target className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h4 className="text-lg font-medium text-gray-900 mb-2">No Therapeutic Goals Set</h4>
            <p className="text-gray-600 mb-4">
              Add therapeutic goals to track what {patient.name} wishes to achieve from therapy.
            </p>
            <Button onClick={handleAddGoal}>
              <Plus className="h-4 w-4 mr-2" />
              Add First Goal
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {goals.map((goal) => (
              <div
                key={goal.goalId}
                className="bg-white p-4 md:p-5 rounded-lg border border-gray-200 hover:shadow-md transition-shadow"
              >
                <div className="relative">
                  {/* Edit icon in top-right */}
                  <div className="absolute top-0 right-0">
                    <Button
                      onClick={() => handleEditGoal(goal)}
                      variant="ghost"
                      size="small"
                      className="p-1.5 hover:bg-gray-100 rounded"
                      style={{color: 'var(--color-primary)'}}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                  </div>
                  
                  {/* Content area with padding for icon */}
                  <div className="pr-12">
                    <h4 className="text-base md:text-lg font-semibold text-gray-900 mb-2">{goal.title}</h4>
                    <p className="text-sm md:text-base text-gray-700 mb-3">{goal.description}</p>
                    {goal.notes && (
                      <p className="text-sm text-gray-600 italic mb-3">Note: {goal.notes}</p>
                    )}
                    {goal.targetDate && (
                      <div className="flex items-center text-xs text-gray-500 mb-2">
                        <Calendar className="h-3 w-3 mr-1" />
                        Target: {therapeuticGoalsService.formatDateForDisplay(goal.targetDate)}
                      </div>
                    )}
                    {/* Status badge and date at bottom-left */}
                    <div className="flex items-center gap-3 mt-4">
                      {goal.status === 'active' ? (
                        <Badge
                          size="small"
                          className="bg-purple-100 text-purple-800 border-0"
                        >
                          Active
                        </Badge>
                      ) : (
                        <Badge
                          variant={
                            goal.status === 'achieved' ? 'success' :
                            goal.status === 'in-progress' ? 'accent' : 'default'
                          }
                          size="small"
                        >
                          {goal.status === 'achieved' ? 'Achieved' : 'In Progress'}
                        </Badge>
                      )}
                      <p className="text-xs text-gray-500">
                        Added: {therapeuticGoalsService.formatDateForDisplay(goal.createdAt)}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {showAddModal && (
        <TherapeuticGoalModal
          patient={patient}
          goal={editingGoal}
          onSave={handleSaveGoal}
          onClose={() => {
            setShowAddModal(false);
            setEditingGoal(null);
          }}
          saving={saving}
        />
      )}
    </>
  );
};

/**
 * Therapeutic Goal Modal
 * Form for adding or editing therapeutic goals
 */
const TherapeuticGoalModal = ({ patient, goal, onSave, onClose, saving }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    status: 'active',
    targetDate: '',
    notes: ''
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (goal) {
      setFormData({
        title: goal.title || '',
        description: goal.description || '',
        status: goal.status || 'active',
        targetDate: goal.targetDate ? new Date(goal.targetDate).toISOString().split('T')[0] : '',
        notes: goal.notes || ''
      });
    }
  }, [goal]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.title.trim()) {
      newErrors.title = 'Goal title is required';
    } else if (formData.title.trim().length < 3) {
      newErrors.title = 'Goal title must be at least 3 characters';
    } else if (formData.title.trim().length > 100) {
      newErrors.title = 'Goal title must be less than 100 characters';
    }

    if (!formData.description.trim()) {
      newErrors.description = 'Goal description is required';
    } else if (formData.description.trim().length < 10) {
      newErrors.description = 'Goal description must be at least 10 characters';
    } else if (formData.description.trim().length > 500) {
      newErrors.description = 'Goal description must be less than 500 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    const goalData = {
      title: formData.title.trim(),
      description: formData.description.trim(),
      status: formData.status,
      notes: formData.notes.trim() || undefined,
      targetDate: formData.targetDate ? new Date(formData.targetDate).toISOString() : undefined
    };

    onSave(goalData);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold">
            {goal ? 'Edit Therapeutic Goal' : 'Add Therapeutic Goal'}
          </h3>
          <Button variant="ghost" onClick={onClose} size="small">
            <X className="h-4 w-4" />
          </Button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Goal Title <span className="text-red-500">*</span>
            </label>
            <Input
              type="text"
              value={formData.title}
              onChange={(e) => handleInputChange('title', e.target.value)}
              placeholder="e.g., Reduce Anxiety Levels"
              error={errors.title}
            />
            {errors.title && (
              <p className="text-red-600 text-sm mt-1">{errors.title}</p>
            )}
            <p className="text-xs text-gray-500 mt-1">
              {formData.title.length}/100 characters
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Goal Description <span className="text-red-500">*</span>
            </label>
            <textarea
              className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              rows="4"
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              placeholder="Describe the therapeutic goal in detail (e.g., 'Learn and practice coping strategies to reduce anxiety levels during stressful situations')"
            />
            {errors.description && (
              <p className="text-red-600 text-sm mt-1">{errors.description}</p>
            )}
            <p className="text-xs text-gray-500 mt-1">
              {formData.description.length}/500 characters
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Status
            </label>
            <Select
              value={formData.status}
              onChange={(e) => handleInputChange('status', e.target.value)}
              options={[
                { value: 'active', label: 'Active' },
                { value: 'in-progress', label: 'In Progress' },
                { value: 'achieved', label: 'Achieved' }
              ]}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Target Date (Optional)
            </label>
            <Input
              type="date"
              value={formData.targetDate}
              onChange={(e) => handleInputChange('targetDate', e.target.value)}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Notes (Optional)
            </label>
            <textarea
              className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              rows="3"
              value={formData.notes}
              onChange={(e) => handleInputChange('notes', e.target.value)}
              placeholder="Additional notes or context about this goal..."
            />
          </div>

          <div className="flex space-x-3 pt-4">
            <Button type="button" variant="outline" onClick={onClose} className="flex-1">
              Cancel
            </Button>
            <Button type="submit" variant="primary" className="flex-1" disabled={saving}>
              {saving ? (
                <>
                  <Spinner size="small" className="mr-2" />
                  {goal ? 'Updating...' : 'Saving...'}
                </>
              ) : (
                goal ? 'Update Goal' : 'Add Goal'
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TherapeuticGoals;

